import * as React from 'react';
import { Route, Switch, Redirect } from 'react-router';
import { History } from 'history';
import { Router } from 'react-router-dom';
import Dashboard from './Dashboard';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { State, Toast } from '../redux/state';
import * as selectors from '../redux/selectors';
import actions from '../redux/actions';
import { connect } from 'react-redux';
import ToastComponent from '../shared/Toast';
import Home from './Home';
import { Routes, makePathnames } from '../utils/routerHelper';
import About from './About';
import AboutSF from './About/sf';
import Faq from './Faq';
import Blog from './Blog';
import Share from './Share';
import Missing from './Missing';
import { withLocalization, LProps } from '../utils/wrappers';
import Entity from './Entity';
import Pledge from './Pledge';
import User from './User';
import Account from './Account';
import StoryPage from './StoryPage';
import TeamsNewPage from './TeamsNew';
import EntityEdit from './EntityEdit';
import Teams from './Teams';
import Departments from './Departments';
import EntityBlog from './EntityBlog';
import BlogEntry from './BlogEntry';
import BlogEntryEdit from './BlogEntryEdit';
import BlogEntryNewPage from './BlogEntryNew';
import Pledges from './Pledges';
import PledgeCategory from './PledgeCategory';
import Calculator from './Calculator';
import CompetitionTopUserScores from './CompetitionTopUserScores';
import EntityTopUserScores from './EntityTopUserScores';
import Survey from './Survey';
import MissingCompetition from './MissingCompetition';
import GlobalAbout from './GlobalAbout';
import GlobalAboutSF from './GlobalAbout/sf';
import ActionSavings from './ActionSavings';
import CatalogBreakdown from './CatalogBreakdown';
import EmailTemplates from './EmailTemplates';
import Totals from './Totals';
import Moderation from './Moderation';
import HeroNominations from './HeroNominations';
import SendMessage from './SendMessage';
import SendMessageDetail from './SendMessage/Detail';
import AdminGuide from './AdminGuide';
import Accessibility from './Accessibility';
import Communications from './Communications';
import CommunicationsScheduled from './Communications/Scheduled';
import Notifications from './Notifications';

interface ManualProps {
  history: History;
}

interface Props extends ManualProps {
  initialized: boolean;
  spinner: boolean;
  toast: Partial<Toast>;
  isLoggedIn: boolean;
  hasJoined: boolean;
  isImpersonating: boolean;
  initializeEntityPage: (h: History) => void;
}

const getHomeComponent = (isLoggedIn: boolean, hasJoined: boolean, sessionHydrating: boolean) => {
  // While /me is in flight we only have a persisted token (not competitionUser yet).
  // Assume joined so we keep Dashboard up instead of flashing Join/login.
  if (isLoggedIn && (hasJoined || sessionHydrating)) {
    return Dashboard;
  }
  return Home;
};

const Layout = ({
  initialized,
  spinner,
  toast,
  isLoggedIn,
  hasJoined,
  isImpersonating,
  history,
  t
}: LProps<Props>) => {
  const paths = makePathnames(t);
  const homePaths = [
    Routes.Login, Routes.Signup, Routes.ForgotPassword,
    Routes.PasswordReset, Routes.Email, Routes.Join
  ]; // .map((route) => paths[route]).join('|');
  const acctPaths = [
    Routes.Account, Routes.Email, Routes.NotificationSettings
  ]; // .map((route) => paths[route]).join('|');
  const joinPath = paths[Routes.Join];
  const dashboardPath = paths[Routes.Dashboard];
  // Token is restored instantly; competitionUser arrives from getMe. Avoid Join/login flash.
  const sessionHydrating = Boolean(isLoggedIn && !hasJoined && spinner);

  return (
    <Router history={history}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
        {isImpersonating && (
          <div style={{
            backgroundColor: '#ff4d4f',
            color: 'white',
            textAlign: 'center',
            padding: '8px',
            fontWeight: 'bold',
            flexShrink: 0
          }}>
            You are impersonating a user. This view is read-only.
          </div>
        )}
        <div id="layout">
          {spinner && <div
            id="layout__overlay"
          >
            <div id="layout__overlay__bg" />
            <div id="layout__overlay__spinner" />
          </div>}
          {initialized &&
            <Switch>
              <Route key="global-about" path={paths[Routes.GlobalAbout]} component={GlobalAbout} exact={true} />
              <Route key="global-about-sf" path={paths[Routes.GlobalAboutSF]} component={GlobalAboutSF} exact={true} />
              {isLoggedIn && hasJoined && <Redirect key="redirect-login" from={paths[Routes.Login]} to={dashboardPath} />}
              {isLoggedIn && hasJoined && <Redirect key="redirect-signup" from={paths[Routes.Signup]} to={dashboardPath} />}
              {isLoggedIn && !hasJoined && !sessionHydrating && <Redirect key="redirect-login-join" from={paths[Routes.Login]} to={joinPath} />}
              {isLoggedIn && !hasJoined && !sessionHydrating && <Redirect key="redirect-signup-join" from={paths[Routes.Signup]} to={joinPath} />}
              {!isLoggedIn && <Redirect key="redirect-account" from={`(${acctPaths})`} to={paths[Routes.Login]} exact={true} />}
              {/** Content routes */}
              <Route key="root-home" path={'/'} render={(props) =>
                <Home {...props} readOnly />
              } exact={true} />
              <Route key="dashboard" path={paths[Routes.Dashboard]} component={getHomeComponent(isLoggedIn, hasJoined, sessionHydrating)} exact={true} />
              <Route key="competition-user-scores" path={paths[Routes.CompetitionUserScores]} component={CompetitionTopUserScores} exact={true} />
              <Route key="entity-user-scores" path={paths[Routes.CompetitionUserScoresForEntity]} component={EntityTopUserScores} exact={true} />

              <Route key="about" path={paths[Routes.About]} component={About} exact={true} />
              <Route key="about-sf" path={paths[Routes.AboutSF]} component={AboutSF} exact={true} />
              <Route key="accessibility" path={paths[Routes.Accessibility]} component={Accessibility} exact={true} />
              <Route key="faq" path={paths[Routes.Faq]} component={Faq} exact={true} />
              <Route key="calculator" path={paths[Routes.Calculator]} component={Calculator} exact={true} />
              <Route key="action-savings" path={paths[Routes.ActionSavings]} component={ActionSavings} exact={true} />
              <Route key="catalog-breakdown" path={paths[Routes.CatalogBreakdown]} component={CatalogBreakdown} exact={true} />
              <Route key="email-templates" path={paths[Routes.EmailTemplates]} component={EmailTemplates} exact={true} />
              <Route key="totals" path={paths[Routes.Totals]} component={Totals} exact={true} />
              <Route key="moderation" path={paths[Routes.Moderation]} component={Moderation} exact={true} />
              <Route key="hero-nominations" path={paths[Routes.HeroNominations]} component={HeroNominations} exact={true} />
              <Route key="send-message-detail" path={`${paths[Routes.SendMessage]}/:broadcastID`} component={SendMessageDetail} exact={true} />
              <Route key="send-message" path={paths[Routes.SendMessage]} component={SendMessage} exact={true} />
              <Route key="admin-guide" path={paths[Routes.AdminGuide]} component={AdminGuide} exact={true} />
              <Route key="communications-scheduled" path={paths[Routes.CommunicationsScheduled]} component={CommunicationsScheduled} exact={true} />
              <Route key="communications" path={paths[Routes.Communications]} component={Communications} exact={true} />
              <Route key="notifications" path={paths[Routes.Notifications]} component={Notifications} exact={true} />

              <Route key="pledge" path={paths[Routes.Pledge]} component={Pledge} exact={true} />
              <Route key="pledges" path={paths[Routes.Pledges]} component={Pledges} exact={true} />
              <Route key="pledge-category" path={paths[Routes.PledgeCategory]} component={PledgeCategory} exact={true} />
              <Route key="user" path={paths[Routes.User]} component={User} exact={true} />

              <Route key="entity" path={paths[Routes.Entity]} component={Entity} exact={true} />
              <Route key="entity-edit" path={paths[Routes.EntityEdit]} component={EntityEdit} exact={true} />
              <Route key="entity-blog" path={paths[Routes.EntityBlog]} component={EntityBlog} exact={true} />

              <Route key="teams" path={paths[Routes.Teams]} component={Teams} exact={true} />
              <Route key="teams-new" path={paths[Routes.TeamsNew]} component={TeamsNewPage} exact={true} />
              <Route key="departments" path={paths[Routes.Departments]} component={Departments} exact={true} />

              <Route key="story" path={paths[Routes.Story]} component={StoryPage} exact={true} />
              <Route key="survey" path={paths[Routes.Survey]} component={Survey} exact={true} />

              <Route key="blog" path={paths[Routes.Blog]} component={Blog} exact={true} />
              <Route key="blog-entry" path={paths[Routes.BlogEntry]} component={BlogEntry} exact={true} />
              <Route key="blog-entry-edit" path={paths[Routes.BlogEntryEdit]} component={BlogEntryEdit} exact={true} />
              <Route key="blog-entry-new" path={paths[Routes.BlogEntryNew]} component={BlogEntryNewPage} exact={true} />

              {acctPaths.map((p) =>
                <Route key={p} path={paths[p]} component={Account} exact={true} />
              )}

              {/** Authentication routes */}
              {homePaths.map((p) =>
                <Route key={p} path={paths[p]} component={Home} exact={true} />
              )}

              {/** Static */}
              <Route key="share" path={paths[Routes.Share]} component={Share} exact={true} />

              <Route key="missing-competition" path="/" component={MissingCompetition} exact={true} />

              {/** No route matches */}
              <Route key="missing" component={Missing} />
            </Switch>
          }
          {toast.on && <ToastComponent />}
        </div>
      </div>
    </Router>
  );
};

const mapStateToProps = (state: State, _ownProps: Props): Partial<Props> => ({
  initialized: state.initialized,
  spinner: state.spinner,
  toast: state.toast,
  isLoggedIn: selectors.isLoggedIn(state),
  hasJoined: selectors.hasJoined(state),
  isImpersonating: selectors.isImpersonating(state)
});
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  initializeEntityPage: actions.initializeEntityPage
}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocalization('routes')
)(Layout) as React.ComponentClass<ManualProps>;
